<template>
  <div class="flex flex-col h-full min-h-0 flex-1">
    <div class="flex-1 h-full flex overflow-auto min-h-0">
      <ImageAnnotator
        :image="element.figureUrl"
        :annotations="annotatorAnnotations"
        v-model="activeAnnotationId"
        @annotation-updated="onAnnotationUpdated"
      />
      <div class="flex flex-col w-90 shrink-0 bg-white">
        <h3
          class="text-sm font-medium border-t border-mid-neutral-50 py-4 px-6 shadow-xs"
        >
          Elements
        </h3>
        <Combobox
          :items="filteredChildren"
          can-add
          :display="(item) => item.name"
          @add-item="createElement"
          @update:model-value="onSelectElement"
          ref="comboboxRef"
          class="py-4 px-4"
        />
        <div class="overflow-auto flex flex-col gap-2">
          <div
            v-for="(annotatedChild, index) in annotatedChildren"
            :key="annotatedChild.annotation.id"
            class="px-6 py-2 flex items-center gap-2"
          >
            <button
              v-if="!annotatedChild.annotation.points.length"
              @click="activeAnnotationId = annotatedChild.annotation.id"
              class="text-left p-0.5 rounded-full hover:bg-primary-500 hover:text-white flex items-center"
              :class="{
                'bg-primary-500 text-white':
                  activeAnnotationId === annotatedChild.annotation.id,
              }"
            >
              <span class="material-symbols-outlined">adjust</span>
            </button>
            <div
              v-else
              class="text-left p-2 w-7 h-7 justify-center rounded-full bg-primary-500 text-white flex items-center"
            >
              {{ buildAnnotationLabel(index) }}
            </div>
            <div>{{ annotatedChild.element.name }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import type { Element, FigureAnnotation, PolyPoint } from "types";
import { ImageAnnotator, Combobox } from "ui-components";
import { computed, nextTick, ref, useTemplateRef } from "vue";
import { generateId } from "utils";
import { useFunctionalElementFigureAnnotationsState } from "states/functionalElements";

const { element, children } = defineProps<{
  element: Element;
  children: Element[];
}>();
const filteredChildren = computed(() =>
  children.filter((child) => {
    const hasAnnotation = annotations.value.some(
      (a) => a.elementId === child.id
    );
    return !hasAnnotation;
  })
);
const annotations = useFunctionalElementFigureAnnotationsState(element);
const activeAnnotationId = ref("");
const comboboxElement = useTemplateRef("comboboxRef");
const annotatorAnnotations = computed(() =>
  annotations.value.map((a) => ({
    id: a.id,
    text: element.name,
    points: a.points,
  }))
);

const annotatedChildren = computed<
  {
    element: Element;
    annotation: FigureAnnotation;
  }[]
>(() =>
  annotations.value
    .map((a) => {
      const child = children.find((c) => c.id === a.elementId);
      return child ? { element: child, annotation: a } : null;
    })
    .filter((c) => !!c)
);

function addElementAnnotation(elementId: string) {
  const annotation = {
    id: generateId(),
    points: [],
    elementId: elementId,
    parentElementId: element.id,
  };
  annotations.value.push(annotation);
  return annotation;
}

async function createElement(name: string) {
  console.log("createElement", name);
  await nextTick();
  comboboxElement.value?.clear();
}

async function onSelectElement(elementId: string | null) {
  if (!elementId) return;
  await nextTick();
  comboboxElement.value?.clear();
  const annotation = addElementAnnotation(elementId);
  activeAnnotationId.value = annotation.id;
}

function onAnnotationUpdated({
  points,
  id,
}: {
  id: string;
  points: PolyPoint[];
}) {
  const index = annotations.value.findIndex((a) => a.id === id);
  if (index !== -1) {
    annotations.value[index].points = JSON.parse(JSON.stringify(points));
  }
}

function buildAnnotationLabel(index: number) {
  let s = "";
  index += 1;
  while (index > 0) {
    index--;
    s = String.fromCharCode(65 + (index % 26)) + s;
    index = Math.floor(index / 26);
  }
  return s;
}
</script>
